import { GoogleCalendarEvent, MicrosoftCalendarEvent } from "../types/calendar";

export interface AttendeeData {
  email: string;
  name?: string;
  responseStatus?: string;
  isOrganizer?: boolean;
}

/**
 * Normalizes attendee data from Google Calendar API response
 */
export function normalizeGoogleAttendees(attendees?: GoogleCalendarEvent['attendees']): AttendeeData[] {
  if (!attendees) return [];
  
  return attendees.map(attendee => ({
    email: attendee.email,
    name: attendee.displayName,
    responseStatus: attendee.responseStatus,
    isOrganizer: attendee.organizer
  }));
}

/**
 * Normalizes attendee data from Microsoft Calendar API response
 */
export function normalizeMicrosoftAttendees(attendees?: MicrosoftCalendarEvent['attendees']): AttendeeData[] {
  if (!attendees) return [];
  
  return attendees.map(attendee => ({
    email: attendee.emailAddress?.address || '',
    name: attendee.emailAddress?.name,
    responseStatus: attendee.status?.response,
    isOrganizer: attendee.type === 'organizer'
  }));
}

/**
 * Normalizes organizer data from Google Calendar API response
 */
export function normalizeGoogleOrganizer(organizer?: GoogleCalendarEvent['organizer']): AttendeeData | null {
  if (!organizer?.email) return null;
  
  return {
    email: organizer.email,
    name: organizer.displayName,
    responseStatus: 'accepted', // Organizer is always accepted
    isOrganizer: true
  };
}

/**
 * Normalizes organizer data from Microsoft Calendar API response
 */
export function normalizeMicrosoftOrganizer(organizer?: MicrosoftCalendarEvent['organizer']): AttendeeData | null {
  if (!organizer?.emailAddress?.address) return null;
  
  return {
    email: organizer.emailAddress.address,
    name: organizer.emailAddress.name,
    responseStatus: 'accepted', // Organizer is always accepted
    isOrganizer: true
  };
}

/**
 * Converts legacy string array attendees to AttendeeData format
 */
export function normalizeStringAttendees(attendees?: string[]): AttendeeData[] {
  if (!attendees) return [];
  
  return attendees.map(email => ({
    email,
    name: undefined,
    responseStatus: undefined,
    isOrganizer: false
  }));
}

/**
 * Converts legacy string organizer to AttendeeData format
 */
export function normalizeStringOrganizer(organizer?: string): AttendeeData | null {
  if (!organizer) return null;
  
  return {
    email: organizer,
    name: undefined,
    responseStatus: 'accepted',
    isOrganizer: true
  };
}

/**
 * Extracts attendee email addresses from AttendeeData array
 */
export function extractAttendeeEmails(attendees: AttendeeData[]): string[] {
  return attendees.map(a => a.email);
}

/**
 * Groups attendees by response status
 */
export function groupAttendeesByStatus(attendees: AttendeeData[]): {
  accepted: AttendeeData[];
  declined: AttendeeData[];
  tentative: AttendeeData[];
  noResponse: AttendeeData[];
} {
  const accepted: AttendeeData[] = [];
  const declined: AttendeeData[] = [];
  const tentative: AttendeeData[] = [];
  const noResponse: AttendeeData[] = [];

  attendees.forEach(attendee => {
    const status = attendee.responseStatus?.toLowerCase();
    
    switch (status) {
      case 'accepted':
        accepted.push(attendee);
        break;
      case 'declined':
        declined.push(attendee);
        break;
      case 'tentative':
        tentative.push(attendee);
        break;
      default:
        noResponse.push(attendee);
        break;
    }
  });

  return { accepted, declined, tentative, noResponse };
}
